/**
 * Centralized API wrapper with automatic token refresh
 *
 * Features:
 * - Automatic token refresh on 401 responses
 * - Request retry after successful token refresh
 * - Concurrent request handling (avoids multiple refresh calls)
 * - Session expiry detection and callback
 */

// ============================================================================
// Types
// ============================================================================

export interface ApiResponse<T = unknown> {
  response: Response | null;
  data: T | null;
}

export type SessionExpiredCallback = () => void;

export type RefreshSubscriber = (success: boolean) => void;

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

// ============================================================================
// State
// ============================================================================

// Track if we're currently refreshing to avoid multiple concurrent refresh calls
let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

// Callback for when session expires (set by the app)
let onSessionExpired: SessionExpiredCallback | null = null;

// Flag to prevent multiple session expiry callbacks
let sessionExpiredCalled = false;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get base URL from environment
 */
const getBaseUrl = (): string => {
  return process.env.REACT_APP_API_URL || "http://localhost:5000";
};

/**
 * Set the callback to be called when session expires (refresh token also expired)
 * @param callback - Function to call on session expiry (e.g., redirect to login)
 */
const setSessionExpiredCallback = (callback: SessionExpiredCallback): void => {
  onSessionExpired = callback;
};

/**
 * Subscribe to token refresh completion
 * @param callback - Function to call when refresh completes
 */
const subscribeTokenRefresh = (callback: RefreshSubscriber): void => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers that token has been refreshed
 * @param success - Whether refresh was successful
 */
const onTokenRefreshed = (success: boolean): void => {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
};

/**
 * Check if we're on the login or register page
 * @returns True if on login/register page
 */
const isOnAuthPage = (): boolean => {
  const path = window.location.pathname;
  return path === "/login" || path === "/register" || path === "/forgot-password" || path === "/reset-password";
};

/**
 * Check if we're on a public page where session expiry shouldn't redirect
 * @returns True if on a public page
 */
const isOnPublicPage = (): boolean => {
  const path = window.location.pathname;
  return path === "/" || path === "/home" || path === "/login" || path === "/register" || path === "/forgot-password" || path === "/reset-password";
};

/**
 * Check if there's evidence the user was logged in (has localStorage data)
 * @returns True if user appears to have been logged in
 */
const wasUserLoggedIn = (): boolean => {
  return !!(localStorage.getItem("email") || localStorage.getItem("first_name"));
};

// ============================================================================
// Token Refresh Logic
// ============================================================================

/**
 * Attempt to refresh the access token using the refresh token cookie
 * @returns Whether refresh was successful
 */
const refreshToken = async (): Promise<boolean> => {
  try {
    const refreshUrl = `${getBaseUrl()}/api/v1/auth/refresh`;

    const response = await fetch(refreshUrl, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "include", // Include cookies (refresh token)
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      console.log("Token refreshed successfully");
      return true;
    } else {
      console.log("Token refresh failed:", response.status);
      return false;
    }
  } catch (error) {
    console.error("Token refresh error:", error);
    return false;
  }
};

/**
 * Handle 401 response - attempt token refresh and retry
 * @param retryRequest - Function to retry the original request
 * @returns Response from retried request or null
 */
const handle401 = async (
  retryRequest: () => Promise<Response>
): Promise<Response | null> => {
  // Don't try to refresh if we're on auth pages (login/register)
  if (isOnAuthPage()) {
    console.log("On auth page, skipping token refresh");
    return null;
  }

  // Only attempt refresh if user was previously logged in
  if (!wasUserLoggedIn()) {
    console.log("User was not logged in, skipping token refresh");
    return null;
  }

  if (isRefreshing) {
    // Wait for the ongoing refresh to complete
    return new Promise((resolve) => {
      subscribeTokenRefresh((success) => {
        if (success) {
          resolve(retryRequest());
        } else {
          resolve(null);
        }
      });
    });
  }

  isRefreshing = true;

  try {
    const success = await refreshToken();

    if (success) {
      // Reset the session expired flag on successful refresh
      sessionExpiredCalled = false;
      onTokenRefreshed(true);
      isRefreshing = false;
      return retryRequest();
    } else {
      onTokenRefreshed(false);
      isRefreshing = false;

      // Session expired - notify the app (only once, and only on protected pages)
      if (onSessionExpired && !sessionExpiredCalled && !isOnPublicPage()) {
        sessionExpiredCalled = true;
        onSessionExpired();
      }
      return null;
    }
  } catch (error) {
    onTokenRefreshed(false);
    isRefreshing = false;

    // Session expired - notify the app (only once, and only on protected pages)
    if (onSessionExpired && !sessionExpiredCalled && !isOnPublicPage()) {
      sessionExpiredCalled = true;
      onSessionExpired();
    }
    return null;
  }
};

// ============================================================================
// Core Fetch Wrapper
// ============================================================================

/**
 * Make an authenticated fetch request with automatic token refresh
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @param isRetry - Whether this is a retry after token refresh
 * @returns The fetch response
 */
const authenticatedFetch = async (
  url: string,
  options: FetchOptions = {},
  isRetry = false
): Promise<Response> => {
  const defaultOptions: FetchOptions = {
    mode: "cors",
    cache: "no-cache",
    credentials: "include", // Always include cookies for auth
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    redirect: "follow",
    referrerPolicy: "no-referrer",
  };

  const mergedOptions: FetchOptions = {
    ...defaultOptions,
    ...options,
    headers: { ...defaultOptions.headers, ...options.headers },
  };

  try {
    const response = await fetch(url, mergedOptions);

    // Handle 401 - token expired
    if (response.status === 401 && !isRetry) {
      console.log("Received 401, attempting token refresh...");

      const retryResponse = await handle401(() =>
        authenticatedFetch(url, options, true)
      );

      if (retryResponse) {
        return retryResponse;
      }

      // Return original 401 response if refresh failed
      return response;
    }

    return response;
  } catch (error) {
    throw error;
  }
};

// ============================================================================
// API Methods
// ============================================================================

/**
 * GET request with automatic token refresh
 * @param url - The URL to fetch
 * @returns JSON response or null on error
 */
const Get = async <T = unknown>(url: string): Promise<T | null> => {
  try {
    const response = await authenticatedFetch(url, {
      method: "GET",
    });

    if (response.ok) {
      const data = await response.json();
      return data as T;
    }

    console.error("GET request failed:", response.status);
    return null;
  } catch (error) {
    console.error("GET request error:", error);
    return null;
  }
};

/**
 * POST request with automatic token refresh
 * @param url - The URL to post to
 * @param body - The request body
 * @returns Response and parsed JSON
 */
const Post = async <T = unknown>(
  url: string,
  body: unknown
): Promise<ApiResponse<T>> => {
  try {
    const response = await authenticatedFetch(url, {
      method: "POST",
      body: JSON.stringify(body),
    });

    let data: T | null = null;
    try {
      data = (await response.json()) as T;
    } catch {
      // Response might not have JSON body
    }

    return { response, data };
  } catch (error) {
    console.error("POST request error:", error);
    return { response: null, data: null };
  }
};

/**
 * PUT request with automatic token refresh
 * @param url - The URL to put to
 * @param body - The request body
 * @returns Response and parsed JSON
 */
const Put = async <T = unknown>(
  url: string,
  body: unknown
): Promise<ApiResponse<T>> => {
  try {
    const response = await authenticatedFetch(url, {
      method: "PUT",
      body: JSON.stringify(body),
    });

    let data: T | null = null;
    try {
      data = (await response.json()) as T;
    } catch {
      // Response might not have JSON body
    }

    return { response, data };
  } catch (error) {
    console.error("PUT request error:", error);
    return { response: null, data: null };
  }
};

/**
 * PATCH request with automatic token refresh
 * @param url - The URL to patch
 * @param body - The request body
 * @returns Response and parsed JSON
 */
const Patch = async <T = unknown>(
  url: string,
  body: unknown
): Promise<ApiResponse<T>> => {
  try {
    const response = await authenticatedFetch(url, {
      method: "PATCH",
      body: JSON.stringify(body),
    });

    let data: T | null = null;
    try {
      data = (await response.json()) as T;
    } catch {
      // Response might not have JSON body
    }

    return { response, data };
  } catch (error) {
    console.error("PATCH request error:", error);
    return { response: null, data: null };
  }
};

/**
 * DELETE request with automatic token refresh
 * @param url - The URL to delete
 * @returns Response and parsed JSON
 */
const Delete = async <T = unknown>(url: string): Promise<ApiResponse<T>> => {
  try {
    const response = await authenticatedFetch(url, {
      method: "DELETE",
    });

    let data: T | null = null;
    try {
      data = (await response.json()) as T;
    } catch {
      // Response might not have JSON body
    }

    return { response, data };
  } catch (error) {
    console.error("DELETE request error:", error);
    return { response: null, data: null };
  }
};

/**
 * Raw authenticated fetch - for cases where you need full control
 * Returns the raw response object
 * @param url - The URL to fetch
 * @param options - Fetch options (method, body, headers, etc.)
 * @returns The fetch response
 */
const request = async (
  url: string,
  options: FetchOptions = {}
): Promise<Response> => {
  return authenticatedFetch(url, options);
};

/**
 * Reset session state - call this after successful login
 */
const resetSessionState = (): void => {
  sessionExpiredCalled = false;
  isRefreshing = false;
  refreshSubscribers = [];
};

// ============================================================================
// Export
// ============================================================================

const API = {
  Get,
  Post,
  Put,
  Patch,
  Delete,
  request,
  setSessionExpiredCallback,
  resetSessionState,
  getBaseUrl,
} as const;

export default API;
